import { Branded } from '@library/type-utils'
import { composeFetch, composeParser } from '@library/http'
import {
  addPath,
  auth,
  composeRequest,
  setMethod,
  setQueryParam,
  setServiceUrl,
  sendJSON,
} from '@library/http/requests'
import { extractCoreData } from '@library/http/parsers'

export type UserID = Branded<string, 'UserId'>

export type AddressID = Branded<string, 'AddressId'>

export type AddressState = 'unset' | 'valid' | 'invalid'

export type User = {
  id: UserID
  nameFirst: string
  nameLast: string
  email: string
  phone: string
  marketingDoAllowEmail: string
  marketingDoAllowPhone: string
  marketingDoAllowPost: string
  marketingDoAllowSms: string
  marketingDoAllowThirdparty: string
}

export type UserRequestBody = {
  name_first: string
  name_last: string
  email: string
  phone: string
  marketing_do_allow_email: string
  marketing_do_allow_phone: string
  marketing_do_allow_post: string
  marketing_do_allow_sms: string
  marketing_do_allow_thirdparty: string
}

export type CurrentUser = {
  user: User
}

type UpdatedCurrentUser = {
  updated: User
}

type UserEmailUpdatePayload = {
  email?: string
  password: string
}

export type ShippingAddress = {
  id: AddressID
  name: string
  companyName: string
  line1: string
  line2: string
  line3: string
  town: string
  county: string
  postcode: string
  deleted: boolean
  deliveryInstructions: string
  shippingDefault: boolean
  billingDefault: boolean
  state: AddressState
  premiumDelivery: boolean
}

export type UserBalance = {
  balance: string
}

const currentUserRequest = composeRequest(setServiceUrl('core'), addPath('user/current'), auth)

const shippingAddressRequest = composeRequest(
  currentUserRequest,
  addPath('address'),
  setQueryParam('type', 'shipping'),
)

export const fetchCurrentUser = composeFetch(currentUserRequest, extractCoreData<CurrentUser>())

export const fetchUserShippingAddresses = composeFetch(
  shippingAddressRequest,
  extractCoreData<ShippingAddress[]>(),
)

const userBalanceRequest = composeRequest(currentUserRequest, addPath('balance'))

export const fetchUserBalance = composeFetch(userBalanceRequest, extractCoreData<UserBalance>())

const passwordResetRequest = composeRequest(
  currentUserRequest,
  addPath('passwordReset'),
  setMethod('POST'),
)

export const resetUserPassword = composeFetch(passwordResetRequest, extractCoreData<void>())

const currentUserUpdateRequest = composeRequest(
  currentUserRequest,
  setMethod('PUT'),
  sendJSON<Partial<UserRequestBody>>(),
)

const extractUserResponse = composeParser(extractCoreData<UpdatedCurrentUser>(), (data) => {
  return {
    user: {
      ...data.updated,
    },
  }
})

export const updateCurrentUser = composeFetch(currentUserUpdateRequest, extractUserResponse)

const currentUserEmailUpdateRequest = composeRequest(
  setServiceUrl('core'),
  addPath('v2/user/current'),
  setMethod('PATCH'),
  sendJSON<UserEmailUpdatePayload>(),
  auth,
)

export const updateUserEmail = composeFetch(currentUserEmailUpdateRequest, extractUserResponse)
